import { Box, Flex, Button, BoxProps } from '@chakra-ui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';
import ReactDatePicker, { CalendarContainer } from 'react-datepicker';

import { utils as commonUtils } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';

import './dateRangePicker.scss';

type DateRangePickerProps = {
  onSelectDateRange: (newDateRange: [Date | null, Date | null]) => void;
  currentDateRange: {
    chartDataStartDate: Date | null;
    chartDataEndDate: Date | null;
  };
  width?: string;
};
export const DateRangePicker = ({
  onSelectDateRange,
  currentDateRange,
  width = '50%',
}: DateRangePickerProps) => {
  const { chartDataStartDate, chartDataEndDate } = currentDateRange;
  // Also managing date range state down in this DatePicker so that selecting a start date
  // does not rerender the entire component and call the data refetch.
  // Refetch should only happen when start and end dates are both selected
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({ startDate: chartDataStartDate, endDate: chartDataEndDate });

  const { startDate, endDate } = dateRange;
  return (
    <Box
      width={width || '50%'}
      className="dateRangePickerStyles"
      borderWidth="thin"
      borderRadius="8px"
      height="40px"
    >
      <ReactDatePicker
        showIcon
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(newDateRange) => {
          const [newStartDate, newEndDate] = newDateRange;
          setDateRange({ startDate: newStartDate, endDate: newEndDate });
          onSelectDateRange(newDateRange);
        }}
        isClearable
        dateFormat="MMM d, yyyy"
        placeholderText="Select date range"
        showMonthDropdown
        showYearDropdown
      />
    </Box>
  );
};

// need a generator function that generates a map of key to label + start + end dates
export enum DatePickerPeriod {
  past7Days = 'past7Days',
  past30Days = 'past30Days',
  past90Days = 'past90Days',
  pastYear = 'pastYear',
}

const periodToDaysMap: Record<DatePickerPeriod, number> = {
  past7Days: 7,
  past30Days: 30,
  past90Days: 90,
  pastYear: 365,
};

const DatePickerPeriodButton = ({
  datePickerPeriod,
  onClick,
  currentSelectedDatePickerPeriod,
}: {
  datePickerPeriod: DatePickerPeriod;
  onClick: () => void;
  currentSelectedDatePickerPeriod: DatePickerPeriod | null;
}) => {
  const isCurrentlySelected =
    datePickerPeriod === currentSelectedDatePickerPeriod;

  return (
    <Button
      height="9"
      mr="4"
      px="3"
      py="1"
      fontSize="12px"
      onClick={onClick}
      borderWidth="1px"
      borderColor={colors.gray[200]}
      backgroundColor={isCurrentlySelected ? colors.aurora[400] : 'white'}
      color={isCurrentlySelected ? 'white' : colors.black[70]}
      fontWeight={400}
    >
      {commonUtils.toTitleCase(datePickerPeriod)}
    </Button>
  );
};

type FullDatePickerCalendarContainerProps = {
  currentSelectedDatePickerPeriod: DatePickerPeriod | null;
  onSelectDefaultPeriod: (period: DatePickerPeriod | null) => void;
  children: React.ReactNode | React.ReactNode[];
};
const FullDatePickerCalendarContainer = ({
  currentSelectedDatePickerPeriod,
  onSelectDefaultPeriod,
  children,
}: FullDatePickerCalendarContainerProps) => {
  return (
    <CalendarContainer className="fullDatePicker-calendar-container">
      <Flex pl="2">
        {_.map(DatePickerPeriod, (period) => (
          <DatePickerPeriodButton
            datePickerPeriod={period}
            onClick={() => onSelectDefaultPeriod(period)}
            currentSelectedDatePickerPeriod={currentSelectedDatePickerPeriod}
            key={`datepicker-period-button-${period}`}
          />
        ))}
        <Button
          h="9"
          border="1px solid"
          borderColor={colors.aurora[400]}
          color={colors.aurora[400]}
          bgColor="white"
          px="3"
          py="1"
          fontSize="12px"
          onClick={() => onSelectDefaultPeriod(null)}
        >
          Clear
        </Button>
      </Flex>
      <Box pt="4" position="relative">
        {children}
      </Box>
    </CalendarContainer>
  );
};

type FullDateRangePickerProps = {
  onSelectDateRange: (newDateRange: [Date | null, Date | null]) => void;
  currentDateRange: { startDate: Date; endDate: Date };
  additionalClassNames?: 'transparentBg'[];
  minDate?: Date;
  maxDate?: Date;
} & BoxProps;
export const FullDateRangePicker = ({
  onSelectDateRange,
  currentDateRange,
  additionalClassNames = [],
  minDate,
  maxDate,
  ...remainingBoxProps
}: FullDateRangePickerProps) => {
  // Also managing date range state down in this DatePicker so that selecting a start date
  // does not rerender the entire component and call the data refetch.
  // Refetch should only happen when start and end dates are both selected
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(currentDateRange);
  const [
    selectedDefaultPeriod,
    setSelectedDefaultPeriod,
  ] = useState<DatePickerPeriod | null>(null);

  // check to see if the currently selected dates are one of the default settings
  // even if user manually set the dates in the datepicker, we will be able to show that it is a default
  useEffect(() => {
    setDateRange(currentDateRange);

    const currentDateDay = DateTime.now()
      .setZone('America/New_York')
      .startOf('day');
    const chartDataStartDateDay = DateTime.fromJSDate(
      currentDateRange.startDate,
    )
      .setZone('America/New_York')
      .startOf('day');
    const chartDataEndDateDay = DateTime.fromJSDate(currentDateRange.endDate)
      .setZone('America/New_York')
      .startOf('day');

    if (chartDataEndDateDay.toMillis() === currentDateDay.toMillis()) {
      const dateDiff = chartDataEndDateDay.diff(chartDataStartDateDay, 'days');
      const dateDiffDays = dateDiff.toObject().days;

      switch (dateDiffDays) {
        case 7:
          setSelectedDefaultPeriod(DatePickerPeriod.past7Days);
          break;
        case 30:
          setSelectedDefaultPeriod(DatePickerPeriod.past30Days);
          break;
        case 90:
          setSelectedDefaultPeriod(DatePickerPeriod.past90Days);
          break;
        case 365:
          setSelectedDefaultPeriod(DatePickerPeriod.pastYear);
          break;
        default:
          setSelectedDefaultPeriod(null);
      }
    }
  }, [currentDateRange]);

  const onSelectDefaultPeriod = (period: DatePickerPeriod | null) => {
    if (!period) {
      setDateRange({ startDate: null, endDate: null });
      onSelectDateRange([null, null]);
      setSelectedDefaultPeriod(null);

      return;
    }
    const currentDate = new Date();

    const startDate = DateTime.fromJSDate(currentDate)
      .minus({ days: periodToDaysMap[period] })
      .toJSDate();
    setDateRange({ startDate, endDate: currentDate });
    onSelectDateRange([startDate, currentDate]);
    setSelectedDefaultPeriod(period);
  };

  const { startDate, endDate } = dateRange;
  return (
    <Box
      className={`fullDatePicker-main-container ${additionalClassNames.join(
        ' ',
      )}`}
      borderWidth="thin"
      borderRadius="8px"
      height="40px"
      {...remainingBoxProps}
    >
      <ReactDatePicker
        showIcon
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(newDateRange) => {
          const [newStartDate, newEndDate] = newDateRange;
          setDateRange({ startDate: newStartDate, endDate: newEndDate });
          onSelectDateRange(newDateRange);
        }}
        isClearable
        dateFormat="MMM d, yyyy"
        placeholderText="Select Date Range"
        showPopperArrow={false}
        monthsShown={2}
        disabledKeyboardNavigation // required when showing multiple months to prevent react datepicker bug https://stackoverflow.com/questions/63284785/react-datepicker-remove-coloring-of-same-day-in-other-months
        calendarContainer={({ children }) => (
          <FullDatePickerCalendarContainer
            currentSelectedDatePickerPeriod={selectedDefaultPeriod}
            onSelectDefaultPeriod={onSelectDefaultPeriod}
          >
            {children}
          </FullDatePickerCalendarContainer>
        )}
        minDate={minDate}
        maxDate={maxDate}
      />
    </Box>
  );
};
